import { create } from 'zustand';
import { Tracker } from '../domains/analytics/services/Tracker';
import { CONVERSION, CONVERSION_FUNNEL } from '../domains/analytics/constants/MetricEvents';
import type { AccessDescriptor } from '../domains/access/models/AccessDescriptor';

interface UpgradeModalState {
  isOpen: boolean;
  feature?: string;
  reason?: string;
  templateCategory?: string;
  diagramType?: string;
  descriptor?: AccessDescriptor;
  openModal: (feature: string, reason?: string, templateCategory?: string) => void;
  openCommercialModal: (descriptor: AccessDescriptor, reason?: string) => void;
  closeModal: () => void;
  setDiagramType: (diagramType: string) => void;
}

export const useUpgradeModalStore = create<UpgradeModalState>((set, get) => ({
  isOpen: false,
  feature: undefined,
  reason: undefined,
  templateCategory: undefined,
  diagramType: undefined,
  descriptor: undefined,
  
  openModal: (feature: string, reason?: string, templateCategory?: string) => {
    Tracker.track(CONVERSION.UPGRADE_STARTED, { feature, reason, templateCategory });
    set({
      isOpen: true,
      feature,
      reason,
      templateCategory,
      descriptor: undefined,
    });
  },

  openCommercialModal: (descriptor: AccessDescriptor, reason?: string) => {
    Tracker.track(CONVERSION.UPGRADE_STARTED, {
      feature: descriptor.capability,
      reason,
      product: descriptor.requiredProduct,
      targetPlan: descriptor.requiredPlan,
    });
    set({
      isOpen: true,
      feature: descriptor.capability,
      reason,
      templateCategory: undefined,
      descriptor,
    });
  },
  
  closeModal: () => {
    const { isOpen, feature, reason } = get();
    if (isOpen) {
      // Track upgrade dismissed (conversion funnel)
      Tracker.track(CONVERSION_FUNNEL.UPGRADE_DISMISSED, { feature, reason });
    }
    set({
      isOpen: false,
      feature: undefined,
      reason: undefined,
      templateCategory: undefined,
      descriptor: undefined,
    });
  },

  setDiagramType: (diagramType: string) => {
    set({ diagramType });
  }
}));

// Expose store on window for E2E tests (works in production builds)
// This allows tests to access the store without relying on source maps or dev paths
if (typeof window !== 'undefined') {
  const testWindow = window as typeof window & {
    __upgradeModalStore__?: UpgradeModalState;
    useUpgradeModalStore?: typeof useUpgradeModalStore;
  };
  testWindow.__upgradeModalStore__ = useUpgradeModalStore.getState();
  testWindow.useUpgradeModalStore = useUpgradeModalStore;
}
